import React, { useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { Save, X } from "lucide-react";
import ErrorNotification from "../../ErrorNotification";

const AwsBillEmailTable = ({ title, toList, ccList, refresh, setRefresh, loading }) => {
  const allData = [
    ...(toList || []).map(item => typeof item === 'string' ? { email: item, name: '', type: 'to' } : { ...item, type: 'to' }),
    ...(ccList || []).map(item => typeof item === 'string' ? { email: item, name: '', type: 'cc' } : { ...item, type: 'cc' })
  ];

  const [activeTab, setActiveTab] = useState("to");
  const data = allData.filter(item => item.type === activeTab);
  
  const [isAdding, setIsAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0 });
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const displayData = showAll ? data : data.slice(0, 4);

  let accessToken = useSelector((state) => state.accessToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleSubmit = async (newTo, newCc) => {
    setIsSubmitting(true);
    const query = `
      mutation UpdateAwsBillEmails($to: [EmailRecipientInput], $cc: [EmailRecipientInput]) {
        updateAwsBillEmails(to: $to, cc: $cc) {
          message
        }
      }
    `;
    const variables = {
      to: newTo.map(item => ({ email: item.email, name: item.name })),
      cc: newCc.map(item => ({ email: item.email, name: item.name })),
    };

    await axios
      .post(
        apiUrl,
        { query, variables },
        { headers: { Authorization: accessToken } }
      )
      .then((response) => {
        if (response.data.errors) {
            setError(response.data.errors[0].message || "Failed to update emails.");
        }
      })
      .catch((error) => {
        console.log(error);
        setError("An error occurred while updating.");
      });
    setRefresh(!refresh);
    setIsSubmitting(false);
  };

  const handleAddRow = (e) => {
    e.preventDefault();
    if (!newEmail || !newEmail.includes('@')) {
        setError("Please enter a valid email address.");
        return;
    }
    const safeToList = toList || [];
    const safeCcList = ccList || [];
    
    // Normalize existing data to objects if they are strings
    const normalizedTo = safeToList.map(item => typeof item === 'string' ? { email: item, name: '' } : item);
    const normalizedCc = safeCcList.map(item => typeof item === 'string' ? { email: item, name: '' } : item);

    const newItem = { email: newEmail, name: newName };

    const newTo = activeTab === 'to' ? [...normalizedTo, newItem] : normalizedTo;
    const newCc = activeTab === 'cc' ? [...normalizedCc, newItem] : normalizedCc;
    handleSubmit(newTo, newCc);
    setNewEmail("");
    setNewName("");
    setIsAdding(false);
  };

  const handleDeleteConfirmation = (record) => {
    setRecordToDelete(record);
    setOpenDialog(true);
  };

  const deleteRecord = async () => {
    const safeToList = toList || [];
    const safeCcList = ccList || [];
    
    const normalizedTo = safeToList.map(item => typeof item === 'string' ? { email: item, name: '' } : item);
    const normalizedCc = safeCcList.map(item => typeof item === 'string' ? { email: item, name: '' } : item);

    const newTo = recordToDelete.type === 'to' ? normalizedTo.filter(e => e.email !== recordToDelete.email) : normalizedTo;
    const newCc = recordToDelete.type === 'cc' ? normalizedCc.filter(e => e.email !== recordToDelete.email) : normalizedCc;
    await handleSubmit(newTo, newCc);
    setOpenDialog(false);
    setRecordToDelete(null);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        {error && (
          <ErrorNotification message={error} onClose={() => setError(null)} />
        )}
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h3 className="text-2xl font-bold text-[#003366]">{title}</h3>
              <div className="flex bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => { setActiveTab('to'); setIsAdding(false); }}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'to' 
                      ? 'bg-white text-[#003366] shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  To
                </button>
                <button
                  onClick={() => { setActiveTab('cc'); setIsAdding(false); }}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'cc' 
                      ? 'bg-white text-[#003366] shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  CC
                </button>
              </div>
            </div>
            <button
              onClick={() => setIsAdding(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Email
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#003366]/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Email Address
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isAdding && (
                  <tr className="bg-blue-50">
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Enter name"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003366]"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        required
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="Enter email address"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003366]"
                      />
                    </td>

                    <td className="px-6 py-4 text-center space-x-2">
                      <button
                        onClick={handleAddRow}
                        disabled={newEmail === ""}
                        className={
                          newEmail === ""
                            ? "text-gray-600"
                            : `text-green-600 hover:text-green-800 font-medium`
                        }
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setNewEmail("");
                          setNewName("");
                          setIsAdding(false);
                        }}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}
                {displayData.map((record, i) => (
                  <tr key={i} className="hover:bg-[#003366]/5">
                    <td className="px-6 py-4 whitespace-nowrap text-[#003366]">
                      {record.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-[#003366]">
                      {record.email}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleDeleteConfirmation(record)}
                        className="text-red-600 hover:text-red-800"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            visible: true,
                            x: rect.left + rect.width / 2,
                            y: rect.bottom + 5,
                          });
                        }}
                        onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0 })}
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length > 4 && (
              <div className="text-center mt-4">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-[#003366] hover:text-[#004488] font-medium"
                >
                  {showAll ? "Show Less" : "Show More"}
                </button>
              </div>
            )}
            {data.length === 0 && !isAdding && (
              <div className="text-center py-12">
                <div className="text-[#003366]">
                  {loading ? (
                    <div className="flex justify-center items-center p-8">
                      <CircularProgress size={40} style={{ color: "#003366" }} />
                    </div>
                  ) : (
                    <>
                      <p className="text-lg font-semibold">No Emails Configured</p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
          className="bg-black text-white text-sm rounded px-2 py-1"
        >
          Delete
        </div>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          style: { borderRadius: "8px", padding: "8px", maxWidth: "450px" },
        }}
      >
        <DialogTitle style={{ padding: "16px 24px", fontSize: "1.25rem", fontWeight: 600, color: "#003366" }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: "#4a4a4a", fontSize: "1rem", marginBottom: "8px" }}>
            Are you sure you want to delete the email <b>{recordToDelete?.email}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
          <button
            onClick={() => setOpenDialog(false)}
            style={{
              display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px",
              backgroundColor: "#FFD700", color: "#003366", border: "none", borderRadius: "4px",
              cursor: "pointer", fontSize: "0.875rem", fontWeight: 500,
            }}
          >
            <X size={16} /> Cancel
          </button>
          <button
            onClick={deleteRecord}
            disabled={isSubmitting}
            style={{
              display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px",
              backgroundColor: "#003366", color: "white", border: "none", borderRadius: "4px",
              cursor: "pointer", fontSize: "0.875rem", fontWeight: 500,
            }}
          >
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save size={16} />}
            Confirm
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AwsBillEmailTable;
